import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MetaTags from '../components/MetaTags';
import QuizCard from '../components/QuizCard';
import QuizResult from '../components/QuizResult';
import '../styles/QuizPage.css';

// Icônes SVG inline
const IconHistory = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M19 9l-5 5-4-4-3 3" />
  </svg>
);

const IconCardinal = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const IconRitual = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
    <path d="M2 17l10 5 10-5"></path>
    <path d="M2 12l10 5 10-5"></path>
  </svg>
);

const IconGlobe = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const QuizPage = () => {
  const navigate = useNavigate();
  
  // États pour gérer le quiz
  const [quizCategory, setQuizCategory] = useState(null);
  const [difficulty, setDifficulty] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [timer, setTimer] = useState(null);

  // Si l'API de questions est prête, cette fonction récupère les questions
  const fetchQuestions = async (category, difficulty) => {
    try {
      setLoading(true);
      setError(null);
      
      // Dans une implémentation réelle, ceci serait un appel API
      // Pour le prototype, nous utilisons des questions statiques
      
      // Simuler un délai de chargement
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Questions par catégorie
      const questionsByCategory = {
        histoire: [
          {
            id: 'h1',
            question: "Quel pape a démissionné en 2013, créant la nécessité d'un conclave extraordinaire ?",
            options: ["Jean-Paul II", "Benoît XVI", "François", "Jean XXIII"],
            correctAnswer: 1,
            difficulty: "débutant",
            explanation: "Benoît XVI, né Joseph Ratzinger, a démissionné le 28 février 2013, devenant le premier pape à démissionner depuis Grégoire XII en 1415."
          },
          {
            id: 'h2',
            question: "Combien de tours a nécessité l'élection du Pape François en 2013 ?",
            options: ["3", "5", "9", "12"],
            correctAnswer: 1,
            difficulty: "intermédiaire",
            explanation: "Le cardinal Jorge Mario Bergoglio a été élu au 5ème tour de scrutin, le deuxième jour du conclave."
          },
          {
            id: 'h3',
            question: "Quelle est la majorité requise pour élire un pape lors d'un conclave ?",
            options: ["Majorité simple (50% + 1)", "Majorité des deux tiers", "Majorité des trois quarts", "Unanimité"],
            correctAnswer: 1,
            difficulty: "débutant",
            explanation: "Une majorité des deux tiers des cardinaux électeurs est nécessaire pour élire un pape."
          },
          {
            id: 'h4',
            question: "Quel a été le conclave le plus long de l'histoire moderne ?",
            options: ["Conclave de 1800", "Conclave de 1830-1831", "Conclave de 1903", "Conclave de 1922"],
            correctAnswer: 1,
            difficulty: "expert",
            explanation: "Le conclave de 1830-1831 a duré 54 jours avant d'élire le pape Grégoire XVI."
          },
          {
            id: 'h5',
            question: "Quel signe indique qu'un nouveau pape a été élu ?",
            options: ["Une cloche sonne", "Une fumée blanche", "Un drapeau est hissé", "Un feu d'artifice"],
            correctAnswer: 1,
            difficulty: "débutant",
            explanation: "Une fumée blanche sortant de la cheminée de la Chapelle Sixtine indique qu'un nouveau pape a été élu."
          }
        ],
        cardinaux: [
          {
            id: 'c1',
            question: "Quel âge maximum un cardinal doit-il avoir pour pouvoir voter lors d'un conclave ?",
            options: ["70 ans", "75 ans", "80 ans", "85 ans"],
            correctAnswer: 2,
            difficulty: "débutant",
            explanation: "Les cardinaux de moins de 80 ans à la date du début de la vacance du Saint-Siège peuvent participer à l'élection."
          },
          {
            id: 'c2',
            question: "Quel cardinal est actuellement Secrétaire d'État du Vatican ?",
            options: ["Pietro Parolin", "Luis Antonio Tagle", "Raymond Burke", "Robert Sarah"],
            correctAnswer: 0,
            difficulty: "intermédiaire",
            explanation: "Le cardinal Pietro Parolin est le Secrétaire d'État du Saint-Siège depuis 2013."
          },
          {
            id: 'c3',
            question: "Combien y a-t-il approximativement de cardinaux électeurs dans le Collège des Cardinaux actuel ?",
            options: ["Environ 70", "Environ 120", "Environ 180", "Environ 230"],
            correctAnswer: 1,
            difficulty: "intermédiaire",
            explanation: "Il y a environ 120 cardinaux électeurs, le nombre exact variant au fil du temps."
          },
          {
            id: 'c4',
            question: "Qu'est-ce qu'un cardinal 'in pectore' ?",
            options: ["Un cardinal nommé mais non annoncé publiquement", "Un cardinal qui a démissionné", "Un cardinal qui travaille directement au Vatican", "Un cardinal qui a moins de 50 ans"],
            correctAnswer: 0,
            difficulty: "expert",
            explanation: "Un cardinal 'in pectore' (dans le cœur) est nommé par le pape mais son nom n'est pas révélé publiquement, généralement pour protéger le cardinal ou son ministère dans des régions où les catholiques sont persécutés."
          },
          {
            id: 'c5',
            question: "Quel continent compte actuellement le plus de cardinaux électeurs ?",
            options: ["Afrique", "Amérique", "Asie", "Europe"],
            correctAnswer: 3,
            difficulty: "intermédiaire",
            explanation: "L'Europe compte toujours le plus grand nombre de cardinaux électeurs, bien que le pape François ait nommé de nombreux cardinaux d'autres continents pour diversifier le collège."
          }
        ],
        traditions: [
          {
            id: 't1',
            question: "Où se déroule traditionnellement le conclave ?",
            options: ["Basilique Saint-Pierre", "Chapelle Sixtine", "Castel Gandolfo", "Palais apostolique"],
            correctAnswer: 1,
            difficulty: "débutant",
            explanation: "Le conclave se déroule dans la Chapelle Sixtine du Vatican."
          },
          {
            id: 't2',
            question: "Que signifie le terme 'conclave' ?",
            options: ["Avec une clef", "Conseil sacré", "Choix divin", "Réunion secrète"],
            correctAnswer: 0,
            difficulty: "intermédiaire",
            explanation: "Le terme 'conclave' vient du latin 'cum clave' signifiant 'avec une clef', car les cardinaux sont enfermés à clef jusqu'à l'élection d'un nouveau pape."
          },
          {
            id: 't3',
            question: "Quelle phrase latine est prononcée pour demander à tous ceux qui ne participent pas au conclave de quitter la Chapelle Sixtine ?",
            options: ["Habemus Papam", "Extra omnes", "Sede vacante", "Ite, missa est"],
            correctAnswer: 1,
            difficulty: "intermédiaire",
            explanation: "'Extra omnes' signifie 'tous dehors' en latin, et est prononcé par le Maître des Célébrations Liturgiques Pontificales."
          },
          {
            id: 't4',
            question: "Comment les bulletins de vote sont-ils traités après chaque scrutin lors d'un conclave ?",
            options: ["Ils sont conservés dans les archives du Vatican", "Ils sont brûlés", "Ils sont déchiquetés puis recyclés", "Ils sont remis à chaque cardinal comme souvenir"],
            correctAnswer: 1,
            difficulty: "débutant",
            explanation: "Les bulletins sont brûlés après chaque scrutin, produisant la fameuse fumée blanche (élection) ou noire (pas d'élection)."
          },
          {
            id: 't5',
            question: "Quel serment les cardinaux doivent-ils prêter avant d'entrer en conclave ?",
            options: ["Un serment de fidélité au nouveau pape", "Un serment de pauvreté et d'humilité", "Un serment de secret et de respect des procédures", "Un serment de renoncement à toute ambition personnelle"],
            correctAnswer: 2,
            difficulty: "intermédiaire",
            explanation: "Les cardinaux prêtent serment de suivre les procédures établies et de maintenir le secret absolu sur le déroulement du conclave."
          }
        ],
        géographie: [
          {
            id: 'g1',
            question: "Dans quel pays se trouve la Cité du Vatican ?",
            options: ["France", "Espagne", "Italie", "Suisse"],
            correctAnswer: 2,
            difficulty: "débutant",
            explanation: "La Cité du Vatican est une cité-État enclavée dans Rome, en Italie."
          },
          {
            id: 'g2',
            question: "Quel est le continent d'origine du Pape François ?",
            options: ["Europe", "Amérique du Sud", "Amérique du Nord", "Asie"],
            correctAnswer: 1,
            difficulty: "débutant",
            explanation: "Le Pape François, né Jorge Mario Bergoglio, est originaire d'Argentine, en Amérique du Sud."
          },
          {
            id: 'g3',
            question: "Dans quelle ville italienne s'est tenu le dernier conclave hors de Rome ?",
            options: ["Venise", "Florence", "Naples", "Milan"],
            correctAnswer: 0,
            difficulty: "expert",
            explanation: "Le dernier conclave hors de Rome s'est tenu à Venise en 1799-1800, où Pie VII a été élu."
          },
          {
            id: 'g4',
            question: "De quel pays était originaire le Pape Jean-Paul II ?",
            options: ["Allemagne", "France", "Pologne", "Autriche"],
            correctAnswer: 2,
            difficulty: "débutant",
            explanation: "Jean-Paul II, né Karol Wojtyła, était polonais. Il a été le premier pape non italien depuis 455 ans."
          },
          {
            id: 'g5',
            question: "Quel est le plus petit pays représenté dans le Collège des Cardinaux actuel ?",
            options: ["Monaco", "Malte", "Luxembourg", "Andorre"],
            correctAnswer: 1,
            difficulty: "expert",
            explanation: "Malte, avec une superficie d'environ 316 km², est le plus petit pays représenté dans le Collège des Cardinaux."
          }
        ]
      };
      
      // Filtrer par difficulté si nécessaire
      let filteredQuestions = [...questionsByCategory[category]];
      if (difficulty !== 'tous') {
        filteredQuestions = filteredQuestions.filter(q => q.difficulty === difficulty);
      }
      
      // Mélanger les questions pour plus de variété
      const shuffledQuestions = filteredQuestions.sort(() => 0.5 - Math.random()).slice(0, 5);
      
      setQuestions(shuffledQuestions);
      setCurrentQuestionIndex(0);
      setUserAnswers([]);
      setScore(0);
      setQuizComplete(false);
      
      // Initialiser le timer si les questions sont chargées
      if (shuffledQuestions.length > 0) {
        setTimeLeft(30); // 30 secondes par question
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Erreur lors du chargement des questions:', err);
      setError('Erreur lors du chargement du quiz. Veuillez réessayer.');
      setLoading(false);
    }
  };

  // Démarrer le quiz avec la catégorie et la difficulté sélectionnées
  const startQuiz = (category, difficulty) => {
    setQuizCategory(category);
    setDifficulty(difficulty);
    fetchQuestions(category, difficulty);
  };

  // Enregistrer la réponse de l'utilisateur et passer à la question suivante
  const handleAnswer = (answerIndex) => {
    // Enregistrer la réponse
    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = answerIndex === currentQuestion.correctAnswer;
    
    const newUserAnswers = [...userAnswers, {
      questionId: currentQuestion.id,
      userAnswer: answerIndex,
      correctAnswer: currentQuestion.correctAnswer,
      isCorrect
    }];
    
    setUserAnswers(newUserAnswers);
    
    // Mettre à jour le score
    if (isCorrect) {
      setScore(score + 1);
    }
    
    // Passer à la question suivante ou terminer le quiz
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      // Réinitialiser le timer pour la nouvelle question
      setTimeLeft(30);
    } else {
      // Quiz terminé
      setQuizComplete(true);
      // Arrêter le timer
      if (timer) {
        clearInterval(timer);
        setTimer(null);
      }
    }
  };

  // Réinitialiser et recommencer le quiz
  const restartQuiz = () => {
    setQuizCategory(null);
    setDifficulty(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setScore(0);
    setQuizComplete(false);
    setTimeLeft(null);
    
    // Arrêter le timer
    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }
  };

  // Gérer l'écoulement du temps pour chaque question
  useEffect(() => {
    // Si nous sommes en cours de quiz et que le temps est défini
    if (quizCategory && !quizComplete && timeLeft !== null) {
      // Créer un interval pour décrémenter le temps
      const newTimer = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            // Temps écoulé, passer automatiquement à la question suivante
            clearInterval(newTimer);
            
            // Enregistrer la réponse comme incorrecte (timeout)
            const currentQuestion = questions[currentQuestionIndex];
            const newUserAnswers = [...userAnswers, {
              questionId: currentQuestion.id,
              userAnswer: -1, // -1 pour indiquer un timeout
              correctAnswer: currentQuestion.correctAnswer,
              isCorrect: false
            }];
            
            setUserAnswers(newUserAnswers);
            
            // Passer à la question suivante ou terminer le quiz
            if (currentQuestionIndex < questions.length - 1) {
              setCurrentQuestionIndex(prevIndex => prevIndex + 1);
              // Réinitialiser le timer pour la nouvelle question
              return 30;
            } else {
              // Quiz terminé
              setQuizComplete(true);
              return 0;
            }
          }
          return prevTime - 1;
        });
      }, 1000);
      
      setTimer(newTimer);
      
      // Nettoyer l'intervalle en cas de démontage du composant
      return () => clearInterval(newTimer);
    }
  }, [quizCategory, quizComplete, currentQuestionIndex, questions]);

  // Nettoyage lors du démontage du composant
  useEffect(() => {
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [timer]);

  // Catégories de quiz disponibles avec leurs icônes
  const quizCategories = [
    { id: 'histoire', name: 'Histoire du Conclave', icon: <IconHistory />, description: 'Testez vos connaissances sur l\'histoire des conclaves et des papes au fil des siècles.' },
    { id: 'cardinaux', name: 'Collège des Cardinaux', icon: <IconCardinal />, description: 'Reconnaissez-vous les grands noms du Collège des Cardinaux actuel ?' },
    { id: 'traditions', name: 'Traditions & Procédures', icon: <IconRitual />, description: 'Découvrez les rituels et procédures qui entourent l\'élection d\'un nouveau pape.' },
    { id: 'géographie', name: 'Géographie Vaticane', icon: <IconGlobe />, description: 'Explorez la géographie du Vatican et l\'origine des cardinaux du monde entier.' }
  ];

  // Niveaux de difficulté disponibles
  const difficultyLevels = [
    { id: 'débutant', name: 'Débutant', icon: '🔰', description: 'Questions basiques pour ceux qui découvrent le sujet.' },
    { id: 'intermédiaire', name: 'Intermédiaire', icon: '🏆', description: 'Pour ceux qui ont déjà quelques connaissances sur le Vatican.' },
    { id: 'expert', name: 'Expert', icon: '👑', description: 'Questions difficiles pour les vrais connaisseurs.' },
    { id: 'tous', name: 'Tous niveaux', icon: '🔄', description: 'Mélange de questions de toutes les difficultés.' }
  ];

  return (
    <div className="quiz-page">
      <MetaTags 
        title="Quiz du Conclave" 
        description="Testez vos connaissances sur le Conclave, le Collège des Cardinaux et les traditions du Vatican avec notre quiz interactif."
      />
      
      <div className="quiz-header">
        <h1>Quiz du Conclave</h1>
        <p className="subtitle">Testez vos connaissances sur le Vatican et le Collège des Cardinaux</p>
      </div>
      
      {/* Écran de sélection de catégorie */}
      {!quizCategory && (
        <div className="quiz-selection">
          <h2>Choisissez une catégorie</h2>
          <div className="quiz-categories">
            {quizCategories.map(category => (
              <div 
                key={category.id} 
                className="quiz-category-card"
                onClick={() => setQuizCategory(category.id)}
              >
                <div className="category-icon">{category.icon}</div>
                <h3>{category.name}</h3>
                <p>{category.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Écran de sélection de difficulté */}
      {quizCategory && !difficulty && (
        <div className="quiz-selection">
          <h2>Choisissez un niveau de difficulté</h2>
          <div className="quiz-difficulty-levels">
            {difficultyLevels.map(level => (
              <div 
                key={level.id} 
                className="quiz-difficulty-card"
                onClick={() => startQuiz(quizCategory, level.id)}
              >
                <div className="difficulty-icon">{level.icon}</div>
                <h3>{level.name}</h3>
                <p>{level.description}</p>
              </div>
            ))}
          </div>
          <button className="back-button" onClick={() => setQuizCategory(null)}>
            ← Retour aux catégories
          </button>
        </div>
      )}
      
      {/* Écran de chargement */}
      {loading && (
        <div className="quiz-loading">
          <div className="loader"></div>
          <p>Chargement du quiz...</p>
        </div>
      )}
      
      {/* Affichage des erreurs */}
      {error && (
        <div className="quiz-error">
          <p>{error}</p>
          <button className="restart-button" onClick={restartQuiz}>
            Réessayer
          </button>
        </div>
      )}
      
      {/* Affichage des questions */}
      {!loading && !error && difficulty && !quizComplete && questions.length > 0 && (
        <div className="quiz-question-container">
          <div className="quiz-progress">
            <div className="quiz-progress-text">
              Question {currentQuestionIndex + 1} sur {questions.length}
            </div>
            <div className="quiz-progress-bar">
              <div 
                className="quiz-progress-fill" 
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>
          
          {/* Timer */}
          <div className="quiz-timer">
            <div className="timer-bar">
              <div 
                className={`timer-fill ${timeLeft <= 5 ? 'timer-warning' : ''}`}
                style={{ width: `${(timeLeft / 30) * 100}%` }}
              ></div>
            </div>
            <div className="timer-text">{timeLeft} secondes</div>
          </div>
          
          <QuizCard 
            question={questions[currentQuestionIndex]}
            onAnswer={handleAnswer}
            timer={timeLeft}
          />
          
          <button className="quit-button" onClick={restartQuiz}>
            Abandonner le quiz
          </button>
        </div>
      )}
      
      {/* Affichage des résultats */}
      {quizComplete && (
        <QuizResult 
          score={score}
          totalQuestions={questions.length}
          userAnswers={userAnswers}
          questions={questions}
          category={quizCategories.find(cat => cat.id === quizCategory)}
          difficulty={difficultyLevels.find(diff => diff.id === difficulty)}
          onRestart={restartQuiz}
        />
      )}
    </div>
  );
};

export default QuizPage;